import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Flame, Zap, LogOut, User, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { COURSES } from '../../i18n';
import { cn } from '../../lib/utils';

const Header = ({ 
  title, 
  showBack = false, 
  showStats = true,
  transparent = false,
  className 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProgress } = useApp();
  const { user, logout } = useAuth();

  const isHome = location.pathname === '/';
  const currentCourse = COURSES[user?.selected_course || 'pt-en'];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full',
        transparent ? 'bg-transparent' : 'bg-background/95 backdrop-blur-md border-b border-border',
        className
      )}
    >
      <div className="flex items-center justify-between h-14 px-4 max-w-lg mx-auto">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          {showBack && !isHome ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-9 w-9 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">FO</span>
              </div>
              <span className="font-display font-semibold text-lg hidden sm:block">
                Fuse Online BR
              </span>
            </div>
          )}
          
          {title && showBack && (
            <h1 className="font-display font-semibold text-base truncate max-w-[150px]">
              {title}
            </h1>
          )}

          {/* Course Flag Selector */}
          {!showBack && currentCourse && (
            <button
              onClick={() => navigate('/select-course')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors ml-1"
              data-testid="header-course-selector"
            >
              <span className="text-lg">{currentCourse.flag.split(' → ')[0]}</span>
              <span className="text-muted-foreground text-xs">→</span>
              <span className="text-lg">{currentCourse.flag.split(' → ')[1]}</span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Right Section - User Info & Stats */}
        {showStats && (
          <div className="flex items-center gap-2">
            {/* Streak */}
            <div className="streak-badge">
              <Flame className="w-4 h-4" />
              <span>{userProgress.currentStreak}</span>
            </div>

            {/* XP */}
            <div className="xp-badge">
              <Zap className="w-4 h-4" />
              <span>{userProgress.totalXP}</span>
            </div>

            {/* User Name & Logout */}
            <div className="flex items-center gap-1 ml-1">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-7 h-7 rounded-full object-cover border-2 border-primary/20"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
              <span className="text-sm font-medium max-w-[80px] truncate hidden sm:block">
                {user?.name?.split(' ')[0] || 'Aluno'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                title="Sair"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
